import {PhoneHQIconAvatar, DsAvatar, PhoneHQGlyph, PhoneHQLoader, PhoneHQAvatar, PhoneHQButton, PhoneHQDropdown, PhoneHQInput, SkeletonList} from 'ch3-ui-lib';
import {Colors} from "../../utilis/types/Types";
import {Link, useNavigate} from "react-router-dom";
import {formatDateDayMonth, getTimeAgo} from "../../utilis/timeUtilis";
import {useMemo, useState} from "react";
import RecentCallsDetails from "./RecentCallDetails";
import DeleteConfirmDialog from "./DeleteConfirmDialog";
import {FlagImage} from "react-international-phone";
import {useSelector} from "react-redux";
import {useParams} from "react-router-dom";
import {parsePhoneNumber, PhoneNumber} from 'libphonenumber-js';
import {ClickToCall} from "../../components/ClickToCall/ClickToCall";
import {useDeleteContactByIdMutation, useGetContactByIdQuery} from "../../store/features/Contacts/contactsService";
import {User, useFetchUsersQuery} from "../../store/features/Users/userService";
import {Group} from "../../store/features/groups/groupsService";
import {getFullName} from "../../utilis/getFullName.ts";
import CallFiltersPanel from "../../components/Filters/CallFiltersPanel.tsx";
import {buildQueryParams} from "../../utilis/buildQueryParams";
import {Call, useFetchContactHistoryQuery} from "../../store/features/calls/callsService";
import {RootState, useAppDispatch} from "../../store/store";
import {
    setUsersFilter,
    setSelectedDates,
    setPhoneNumbersFilter
} from "../../store/features/Filters/filtersSlice";
import pipeDriveLogo from "../../assets/pipedrive-logo.svg";
import simCardContactIcon from "../../assets/sim-card-contact.svg";
import noCallsIcon from "../../assets/no-calls.svg";
import ContactPanel from "./ContactPanel";
import OverflowChips from "./OverflowChips";
import {Building, ChevronLeft, Globe, LockKeyhole, Mail, MessageSquare, MoreHorizontal, PencilLine, Phone, Search, Trash2, Users, X} from "lucide-react";
import {clsxMerge} from "../../utilis/classNameUtils.ts";
import {tagBadgeStyles} from "./helpers.tsx";
import {useIntl} from "react-intl";
import {useIsChatEnabledQuery} from "../../services/chatService.ts";
import {useOpenDirectChat} from "../../hooks/useOpenDirectChat.ts";
import {matchColleagueByEmail, resolveContactActionTiles} from "./colleagueChatRules.ts";

type CallTab = "recent" | "missed";

const INITIAL_CALLS_LIMIT = 20;

// 36px action buttons under the contact name (Call, Message, More).
const actionButton = 'inline-flex h-control-md shrink-0 cursor-pointer items-center gap-1.5 rounded-control text-callout';
const CALLS_LIMIT_STEP = 50;

const ContactDetailsContent = ({id}: {id: string | undefined}) => {
    const navigate = useNavigate();
    const userId = useSelector((state: any) => state.auth.userId);

    const [filter, setFilter] = useState('');
    const [limit, setLimit] = useState(INITIAL_CALLS_LIMIT);
    const [activeTab, setActiveTab] = useState<CallTab>("recent");
    const [editPanelOpen, setEditPanelOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteContact] = useDeleteContactByIdMutation();

    const {data: contactData, isLoading: contactLoading} = useGetContactByIdQuery(Number(id));

    const intl = useIntl();
    const {data: isChatEnabled} = useIsChatEnabledQuery();
    const openDirectChat = useOpenDirectChat();

    const isChatEnabledEffective = isChatEnabled !== false;
    const isInternalContact = contactData?.contactType?.type === 'INTERNAL';
    const isRosterActive = isInternalContact && isChatEnabledEffective;
    const {
        data: roster = [],
        isSuccess: isRosterSuccess,
        isError: isRosterError,
        isFetching: isRosterFetching,
    } = useFetchUsersQuery({}, {skip: !isRosterActive, refetchOnMountOrArgChange: 30});
    const matchedColleague = isRosterActive ? matchColleagueByEmail(contactData?.email, roster) : undefined;
    const {showEmail: showEmailRow, showChat: showChatRow} = resolveContactActionTiles({
        isInternalContact,
        isChatEnabled: isChatEnabledEffective,
        hasMatchedColleague: Boolean(matchedColleague),
        isRosterActive,
        isRosterSuccess,
        isRosterError,
        isRosterFetching,
    });

    const handleDeleteContact = async () => {
        if (id) {
            await deleteContact(Number(id));
            navigate('/contacts');
        }
    };
    const dispatch = useAppDispatch();

    const {
        phoneNumbersFilter,
        usersFilter,
        selectedDates,
    } = useSelector((state: RootState) => state.filters);

    // The Calls sidebar stores the chosen line as one object, while the filter
    // panel here stores an array; accept both so a line picked on Calls does
    // not crash this page.
    const selectedPhoneNumbers = Array.isArray(phoneNumbersFilter)
        ? phoneNumbersFilter
        : phoneNumbersFilter ? [phoneNumbersFilter] : [];

    const mainMsisdn = (contactData?.phoneNumbers?.find((p: { main: boolean }) => p.main) ?? contactData?.phoneNumbers?.[0])?.msisdn;

    const phoneNumbers = contactData?.phoneNumbers?.map((phoneNumber: { msisdn: number, main: boolean }) =>
        parsePhoneNumber(`+${phoneNumber.msisdn.toString()}`));

    const callsQueryParams = buildQueryParams({
        contactId: Number(id),
        phrase: filter,
        limit,
        users: usersFilter.map((item: any) => item.id),
        phoneNumbers: selectedPhoneNumbers.map((item: any) => item.msisdn),
        from: selectedDates.from,
        to: selectedDates.to,
        userId,
    });

    const {
        data: historyList,
        isLoading: isLoadingHistory,
    } = useFetchContactHistoryQuery(callsQueryParams, {
        pollingInterval: 10000,
    });

    // Missed-call filtering is done client-side (same rule as the Calls page):
    // an inbound call that was never bridged nor forwarded.
    const displayedCalls = useMemo(() => {
        const groupedCalls = historyList?.groupedCalls;
        if (!groupedCalls) return {};
        if (activeTab !== "missed") return groupedCalls;
        return Object.entries(groupedCalls).reduce((acc: { [key: string]: Call[] }, [date, calls]) => {
            const missed = (calls as Call[]).filter(
                (call) => call.type === 'INBOUND' && !call.bridgetAt && !call.forwardedTo
            );
            if (missed.length) acc[date] = missed;
            return acc;
        }, {});
    }, [historyList, activeTab]);

    const canLoadOlderCalls = historyList !== undefined && historyList.loadedCount >= limit;

    const filterHandlers = {
        onUsersChange: (users: any[]) => dispatch(setUsersFilter(users)),
        onPhoneNumbersChange: (numbers: any[]) => dispatch(setPhoneNumbersFilter(numbers)),
        handleDateSelect: (dateRange: any) => dispatch(setSelectedDates(dateRange)),
    };

    // Access display — same availability badges as the contacts list
    const getAccessLabel = () => {
        if (contactData?.availability === 'PUBLIC') {
            return (
                <span className={clsxMerge(tagBadgeStyles({color: 'indigo'}))}>
                    <Globe size={14} className="text-brand-ink"/>
                    Public
                </span>
            );
        }
        if (contactData?.availability === 'PRIVATE') {
            return (
                <span className={clsxMerge(tagBadgeStyles({color: 'zinc'}))}>
                    <LockKeyhole size={14}/>
                    Only you
                </span>
            );
        }
        if (contactData?.availability === 'SHARED') {
            const sharedItems = [...(contactData?.sharedForGroups ?? []), ...(contactData?.sharedForUsers ?? [])];
            const renderItem = (item: any) => {
                const isGroup = (item as Group).name !== undefined;
                const isUser = (item as User).firstName !== undefined;
                const label = isGroup ? (item as Group).name : isUser ? getFullName((item as User).firstName, (item as User).lastName) : '';
                return (
                    <div className="bg-surface border border-fill-subtle rounded-control h-[28px] px-2 gap-1 inline-flex items-center text-footnote font-medium shadow-sm shrink-0">
                        {isGroup ? (
                            <PhoneHQIconAvatar icon={(item as Group).icon ?? 'group'} color={(item as Group).iconColor ?? Colors.grey} size="20px"/>
                        ) : isUser && (
                            <PhoneHQAvatar size="20px" type="initials" name={label} colorFromName/>
                        )}
                        <span className="text-ink">{label}</span>
                    </div>
                );
            };
            return (
                <OverflowChips
                    items={sharedItems}
                    badgeClassName="bg-surface border border-fill-subtle rounded-control h-[28px] px-2 inline-flex items-center text-footnote font-medium shadow-sm text-ink"
                    renderChip={renderItem}
                    renderTooltipItem={(item: any) => {
                        const isGroup = (item as Group).name !== undefined;
                        const isUser = (item as User).firstName !== undefined;
                        const label = isGroup ? (item as Group).name : isUser ? getFullName((item as User).firstName, (item as User).lastName) : '';
                        return (
                            <div className="flex items-center gap-2">
                                {isGroup ? (
                                    <PhoneHQIconAvatar icon={(item as Group).icon ?? 'group'} color={(item as Group).iconColor ?? Colors.grey} size="20px"/>
                                ) : isUser && (
                                    <PhoneHQAvatar size="20px" type="initials" name={label} colorFromName/>
                                )}
                                <span>{label}</span>
                            </div>
                        );
                    }}
                />
            );
        }
        return null;
    };

    if (contactLoading || isLoadingHistory) {
        return (
            <div className="flex w-full items-center justify-center h-full">
                <PhoneHQLoader/>
            </div>
        );
    }

    if (!contactData) return null;

    return (
        <>
            <div className="flex w-full h-[calc(100vh-var(--header-height))] bg-surface">
                {/* Left pane - Profile */}
                <div className="flex w-[520px] shrink-0 flex-col border-0 border-r border-solid border-line">
                    {/* Top bar: back to the list */}
                    <div className="flex h-header shrink-0 items-center px-4">
                        <button
                            type="button"
                            onClick={() => navigate('/contacts')}
                            className="inline-flex h-7 cursor-pointer items-center gap-1 rounded-chip pl-1 pr-2 text-callout text-ink-secondary hover:bg-surface-muted"
                        >
                            <ChevronLeft size={18} aria-hidden="true" className="!p-0 !text-current"/>
                            Contacts
                        </button>
                    </div>

                    {/* Profile content */}
                    <div className="flex-1 overflow-y-auto px-10 pb-8 pt-2">
                        <div className="flex flex-col gap-6">
                            {/* Avatar + Name */}
                            <div className="flex flex-col items-center gap-3">
                                <div className="relative">
                                    <DsAvatar size={64} name={getFullName(contactData?.firstName, contactData?.lastName)}/>
                                    {contactData?.contactType?.source === 'PIPEDRIVE' && (
                                        <span className="absolute -bottom-1 -right-1 flex items-center justify-center size-5 rounded-full bg-surface border-2 border-white overflow-hidden">
                                            <img className="size-full" src={pipeDriveLogo} alt="Pipedrive"/>
                                        </span>
                                    )}
                                    {contactData?.contactType?.source === 'MOBILE_PRIVATE_CONTACT' && (
                                        <img className="absolute -bottom-1 -right-1 size-5" src={simCardContactIcon} alt="SIM card contact"/>
                                    )}
                                </div>
                                <h1 className="m-0 w-full truncate text-center text-headline text-ink">
                                    {getFullName(contactData?.firstName, contactData?.lastName)}
                                </h1>
                                <div className="mt-1 flex gap-2" data-testid="contacts.details.actions">
                                    {mainMsisdn && (
                                        <ClickToCall
                                            renderAs="button"
                                            msisdn={mainMsisdn}
                                            contact={contactData}
                                            className={`${actionButton} bg-brand px-3.5 text-white hover:bg-brand-hover`}
                                        >
                                            <Phone size={16} aria-hidden="true" className="!p-0 !text-current"/>
                                            Call
                                        </ClickToCall>
                                    )}
                                    {showChatRow && matchedColleague && (
                                        <button
                                            type="button"
                                            onClick={() => openDirectChat(matchedColleague.id)}
                                            className={`${actionButton} bg-surface-muted px-3.5 text-ink hover:bg-line`}
                                        >
                                            <MessageSquare size={16} aria-hidden="true" className="!p-0 !text-current"/>
                                            Message
                                        </button>
                                    )}
                                    {contactData?.contactType?.source !== 'PIPEDRIVE' && (
                                        <PhoneHQDropdown
                                            sideOffset={4}
                                            showCloseIcon
                                            align="end"
                                            trigger={
                                                <button
                                                    type="button"
                                                    aria-label="More actions"
                                                    aria-haspopup="menu"
                                                    className={`${actionButton} w-control-md justify-center bg-surface-muted text-ink hover:bg-line`}
                                                >
                                                    <MoreHorizontal size={18} aria-hidden="true" className="!p-0 !text-current"/>
                                                </button>
                                            }
                                        >
                                            <PhoneHQDropdown.Item onClick={() => setEditPanelOpen(true)}>
                                                <PencilLine size={18} className={'text-ink-muted group-hover:text-white transition-colors'}/>
                                                <span className={'text-tiny px-3 w-32'}>
                                                    Edit
                                                </span>
                                            </PhoneHQDropdown.Item>
                                            <PhoneHQDropdown.Separator/>
                                            <PhoneHQDropdown.Item onClick={() => setDeleteOpen(true)}>
                                                <Trash2 size={18} className={'text-ink-muted group-hover:text-white transition-colors'}/>
                                                <span className={'text-tiny px-3 w-32'}>
                                                    Delete
                                                </span>
                                            </PhoneHQDropdown.Item>
                                        </PhoneHQDropdown>
                                    )}
                                </div>
                            </div>

                            {/* Info table */}
                            <div>
                                <div className="overflow-hidden rounded-[12px] border border-solid border-line bg-surface">
                                    {/* Access row */}
                                    <div className="flex items-center border-0 border-b border-solid border-surface-muted px-2">
                                        <div className="flex min-h-11 w-[200px] shrink-0 items-center gap-2.5 px-2 py-2">
                                            <Users size={16} className="!p-0 text-ink-muted"/>
                                            <span className="text-footnote text-ink-muted-strong">Access</span>
                                        </div>
                                        <div className="min-w-0 flex-1 px-2 py-2 text-footnote font-medium">
                                            {getAccessLabel()}
                                        </div>
                                    </div>

                                    {/* Phone numbers row */}
                                    <div className="flex items-center border-0 border-b border-solid border-surface-muted px-2">
                                        <div className="flex min-h-11 w-[200px] shrink-0 items-center gap-2.5 px-2 py-2">
                                            <Phone size={16} className="!p-0 text-ink-muted"/>
                                            <span className="text-footnote text-ink-muted-strong">Phone numbers</span>
                                        </div>
                                        <div className="min-w-0 flex-1 px-2 py-2 text-footnote font-medium">
                                            <div className="flex flex-wrap gap-2">
                                                {phoneNumbers?.map((phoneNumber: PhoneNumber, index: number) => (
                                                    <div key={index} className="inline-flex h-7 items-center gap-1.5 rounded-chip border border-solid border-line px-2 text-footnote font-medium">
                                                        <FlagImage iso2={phoneNumber.country?.toLowerCase() ?? 'us'} className="w-4 h-3"/>
                                                        <ClickToCall className="text-footnote text-ink" noPrefix={true} msisdn={Number(phoneNumber.number)} icon={false}/>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    {(showChatRow || showEmailRow) && (
                                        <div className="flex items-center border-0 border-b border-solid border-surface-muted px-2">
                                            <div className="flex min-h-11 w-[200px] shrink-0 items-center gap-2.5 px-2 py-2">
                                                {showChatRow ? (
                                                    <MessageSquare size={16} className="!p-0 text-ink-muted"/>
                                                ) : (
                                                    <Mail size={16} className="!p-0 text-ink-muted"/>
                                                )}
                                                <span className="text-footnote text-ink-muted-strong">
                                                    {showChatRow
                                                        ? intl.formatMessage({id: 'contacts.details.chat'})
                                                        : 'Email'}
                                                </span>
                                            </div>
                                            <div className="min-w-0 flex-1 px-2 py-2 text-footnote font-medium">
                                                {showChatRow ? (
                                                    <button
                                                        type="button"
                                                        data-testid="contacts.details.chat-btn"
                                                        aria-label={intl.formatMessage({id: 'contacts.details.chat'})}
                                                        onClick={() => openDirectChat(matchedColleague!.id)}
                                                        className="cursor-pointer text-footnote font-medium text-brand-ink hover:underline"
                                                    >
                                                        {intl.formatMessage({id: 'contacts.details.chat'})}
                                                    </button>
                                                ) : contactData?.email ? (
                                                    <a href={`mailto:${contactData.email}`} className="truncate text-footnote font-medium text-brand-ink hover:underline">
                                                        {contactData.email}
                                                    </a>
                                                ) : (
                                                    <span className="text-footnote text-ink-muted">—</span>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Company row */}
                                    <div className="flex items-center px-2">
                                        <div className="flex min-h-11 w-[200px] shrink-0 items-center gap-2.5 px-2 py-2">
                                            <Building size={16} className="!p-0 text-ink-muted"/>
                                            <span className="text-footnote text-ink-muted-strong">Company</span>
                                        </div>
                                        <div className="min-w-0 flex-1 px-2 py-2 text-footnote font-medium">
                                            <span className="text-footnote font-medium text-ink">
                                                {contactData?.company || '—'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Metadata */}
                            <div className="flex flex-col gap-4">
                                {contactData?.contactType?.type === 'INTERNAL' && contactData?.owner && (
                                    <p className="text-footnote text-ink-muted-strong truncate">
                                        Created by {contactData.owner.firstName} {contactData.owner.lastName}, {formatDateDayMonth(new Date(contactData.createdAt))}
                                    </p>
                                )}
                                {contactData?.contactType?.source === 'MOBILE_PRIVATE_CONTACT' && (
                                    <div className="flex flex-col gap-2 items-start">
                                        <div className="flex items-center gap-1.5">
                                            <img className="size-5" src={simCardContactIcon} alt=""/>
                                            <span className="text-footnote font-medium text-ink">SIM card contact</span>
                                        </div>
                                        <p className="text-footnote text-ink-muted-strong">
                                            {contactData.createdAt
                                                ? `Synchronized ${getTimeAgo(new Date(contactData.createdAt))}`
                                                : 'Never synchronized'}
                                        </p>
                                    </div>
                                )}
                                {contactData?.contactType?.source === 'PIPEDRIVE' && (
                                    <div className="flex flex-col gap-2 items-start">
                                        <div className="flex items-center gap-2">
                                            <span className="text-footnote text-ink-muted-strong">External Contact</span>
                                            <span className="flex items-center gap-1.5">
                                                <img className="size-5" src={pipeDriveLogo} alt="Pipedrive"/>
                                                <span className="text-footnote font-medium text-ink">Pipedrive</span>
                                            </span>
                                        </div>
                                        <p className="text-footnote text-ink-muted-strong">
                                            Imported on {formatDateDayMonth(new Date(contactData.createdAt))}
                                        </p>
                                        {contactData?.url && (
                                            <Link to={contactData.url} target="_blank" className="no-underline">
                                                <span className="inline-flex items-center gap-2 bg-surface-muted hover:bg-line rounded-control min-h-9 px-3 py-2 text-footnote font-medium text-ink cursor-pointer">
                                                    Edit in source system
                                                    <PhoneHQGlyph name="launch" size={12}/>
                                                </span>
                                            </Link>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right pane - Recent calls */}
                <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
                    {/* Tabs + filter */}
                    <div className="flex flex-col gap-2 px-6 pb-2">
                        <div className="flex h-header items-center justify-between">
                            <div className="flex gap-1" role="tablist" aria-label="Calls with this contact">
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={activeTab === "recent"}
                                    onClick={() => setActiveTab("recent")}
                                    className={`h-7 cursor-pointer rounded-chip px-3 text-footnote ${
                                        activeTab === "recent"
                                            ? "bg-brand-tint-subtle font-semibold text-brand-ink"
                                            : "font-medium text-ink hover:bg-surface-muted"
                                    }`}
                                >
                                    Recent calls
                                </button>
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={activeTab === "missed"}
                                    onClick={() => setActiveTab("missed")}
                                    className={`h-7 cursor-pointer rounded-chip px-3 text-footnote ${
                                        activeTab === "missed"
                                            ? "bg-brand-tint-subtle font-semibold text-brand-ink"
                                            : "font-medium text-ink hover:bg-surface-muted"
                                    }`}
                                >
                                    Missed calls
                                </button>
                            </div>
                            <CallFiltersPanel
                                admin={false}
                                items={['USERS', 'DATE', 'PHONE_NUMBERS']}
                                handlers={filterHandlers}
                                selectedUsers={usersFilter}
                                selectedPhoneNumbers={selectedPhoneNumbers}
                                selectedDates={selectedDates}
                            />
                        </div>
                        <PhoneHQInput
                            placeholder="Search..."
                            className="w-full h-[36px]"
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            prefixIcon={<Search size={16}/>}
                            suffixIcon={filter ? (
                                <PhoneHQButton
                                    variant="tertiary"
                                    size={'iconXs'}
                                    onClick={()=> setFilter('')}
                                >
                                    <X size={15} className="!p-0"/>
                                </PhoneHQButton>
                            ) : undefined}
                        />
                    </div>

                    {/* Calls list */}
                    <div className="flex-1 overflow-y-auto">
                        {isLoadingHistory ? (
                            <SkeletonList label="Loading calls" rows={8} rowHeight={52} avatar={false} trailing className="px-6"/>
                        ) : Object.keys(displayedCalls).length === 0 ? (
                            <div className="flex flex-col items-center justify-center gap-3 px-10 pb-12 pt-8">
                                <div className="flex size-12 items-center justify-center rounded-[12px] bg-surface-muted">
                                    <img src={noCallsIcon} alt="" className="size-6"/>
                                </div>
                                <div className="flex flex-col items-center gap-2 text-center">
                                    <h3 className="m-0 text-title text-ink">
                                        No calls yet
                                    </h3>
                                    <p className="m-0 text-callout text-ink-muted">
                                        Start making calls and they'll show up here
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <RecentCallsDetails groupedCalls={displayedCalls}/>
                        )}
                        {canLoadOlderCalls && (
                            <div className="flex justify-center py-3">
                                <PhoneHQButton
                                    variant="tertiary"
                                    onClick={() => setLimit((current) => current + CALLS_LIMIT_STEP)}
                                >
                                    {intl.formatMessage({id: 'contacts.details.loadOlderCalls'})}
                                </PhoneHQButton>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <ContactPanel
                open={editPanelOpen}
                onClose={() => setEditPanelOpen(false)}
                contactId={Number(id)}
            />

            <DeleteConfirmDialog
                open={deleteOpen}
                onOpenChange={setDeleteOpen}
                onConfirm={handleDeleteContact}
            />
        </>
    );
};

const ContactDetails = () => {
    const {id} = useParams<{ id: string }>();
    return <ContactDetailsContent key={id} id={id}/>;
};

export default ContactDetails;
